import { useForm } from "@tanstack/react-form";
import { useEffect, useState } from "react";

import {
  Button,
  Checkbox,
  Select,
  TextArea,
  TextField,
} from "../../../ui/index.ts";
import {
  contactStepSchema,
  type ContactStepValues,
} from "../schemas/create-employee.schema.ts";
import { emergencyContactRelationOptions } from "../../../../../options/emergencyContactRelationOptions.ts";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";
import { formatName } from "../../../../utils/format-name.ts";

import styles from "./contact-step.module.css";

export type ContactStepProps = {
  defaultValues: ContactStepValues;
  loading: boolean;
  onBack: () => void;
  onNext: (v: ContactStepValues) => void;
  formId?: string;
  hideFooter?: boolean;
  onResetReady?: (reset: () => void) => void;
};

const field = (name: keyof ContactStepValues) => name;

function digitsPhone(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

function blankContact(): ContactStepValues {
  return {
    phoneNumber: "",
    alternateNumber: "",
    relationWithAlternateNo: "mother",
    alternateName: "",
    presentAddress: "",
    permanentAddress: "",
  };
}

export function ContactStep({
  defaultValues,
  loading,
  onBack,
  onNext,
  formId,
  hideFooter = false,
  onResetReady,
}: ContactStepProps) {
  const [sameAsPresent, setSameAsPresent] = useState(false);
  const [invalidEmergencyCharacters, setInvalidEmergencyCharacters] =
    useState(false);
  const [invalidEmergencyName, setInvalidEmergencyName] = useState(false);

  const form = useForm({
    defaultValues,
    validators: { onSubmit: contactStepSchema as never },
    onSubmit: async ({ value }) => {
      await onNext(value as ContactStepValues);
    },
  });

  useEffect(() => {
    onResetReady?.(() => {
      setSameAsPresent(false);
      form.reset(blankContact());
    });
  }, [form, onResetReady]);

  return (
    <form
      id={formId}
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >

      <div className={styles.textareas}>
        <form.Field name={field("phoneNumber")}>
          {(f) => (
            <TextField
              id="create-employee-phone"
              label="Contact Number"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={10}
              value={f.state.value}
              onChange={(e) => f.handleChange(digitsPhone(e.target.value))}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("presentAddress")}>
          {(f) => (
            <TextArea
              id="create-employee-present-address"
              label="Current Residential Address"
              rows={4}
              value={f.state.value}
              onChange={(e) => {
                const v = e.target.value;
                f.handleChange(v);
                if (sameAsPresent) {
                  form.setFieldValue("permanentAddress", v);
                }
              }}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("permanentAddress")}>
          {(f) => (
            <div>
              <TextArea
                id="create-employee-permanent-address"
                label="Permanent Address"
                rows={4}
                className={sameAsPresent ? styles.permanentLocked : undefined}
                value={f.state.value}
                onChange={(e) => {
                  if (sameAsPresent) return;
                  f.handleChange(e.target.value);
                }}
                onBlur={f.handleBlur}
                error={formatFormFieldErrors(f.state.meta.errors)}
                disabled={loading}
                readOnly={sameAsPresent}
                required
              />
              <div className={styles.sameRow}>
                <form.Subscribe selector={(s) => s.values.presentAddress}>
                  {(present) => (
                    <Checkbox
                      id="create-employee-same-address"
                      label="Same as Residential Address"
                      checked={sameAsPresent}
                      disabled={loading}
                      onChange={(checked) => {
                        setSameAsPresent(checked);
                        if (checked) {
                          form.setFieldValue("permanentAddress", present);
                        }
                      }}
                    />
                  )}
                </form.Subscribe>
              </div>
            </div>
          )}
        </form.Field>
      </div>
      <div className={styles.grid}>


        <form.Field name={field("alternateName")}>
          {(f) => (
            <TextField
              id="create-employee-alternate-name"
              label="Emergency Contact Name"
              autoComplete="name"
              maxLength={100}
              value={f.state.value}
              onChange={(e) => {
                setInvalidEmergencyName(/\d/.test(e.target.value));
                f.handleChange(
                  formatName(e.target.value.replace(/\d/g, "")),
                );
              }}
              onBlur={f.handleBlur}
              error={
                invalidEmergencyName
                  ? "Emergency contact name must not contain numbers."
                  : formatFormFieldErrors(f.state.meta.errors)
              }
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("alternateNumber")}>
          {(f) => (
            <TextField
              id="create-employee-alternate-number"
              label="Emergency Contact Number"
              type="tel"
              inputMode="numeric"
              autoComplete="off"
              maxLength={10}
              value={f.state.value}
              onChange={(e) => {
                setInvalidEmergencyCharacters(/\D/.test(e.target.value));
                f.handleChange(digitsPhone(e.target.value));
              }}
              onBlur={f.handleBlur}
              error={
                invalidEmergencyCharacters
                  ? "Emergency contact number must contain digits only."
                  : formatFormFieldErrors(f.state.meta.errors)
              }
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("relationWithAlternateNo")}>
          {(f) => (
            <Select
              id="create-employee-relation-alternate"
              label="Relationship with Emergency Contact Person "
              required
              value={f.state.value}
              onChange={(v) => f.handleChange(v as ContactStepValues["relationWithAlternateNo"])}
              options={emergencyContactRelationOptions}
              placeholder="Select Relation"
              disabled={loading}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>



      </div>

      {hideFooter ? null : (
        <div className={styles.actions}>
          <Button type="button" variant="muted-outline" disabled={loading} onClick={onBack}>
            Previous
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Saving…" : "Next"}
          </Button>
        </div>
      )}
    </form>
  );
}
